import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Building2, FileText, Users, Briefcase } from 'lucide-react';
import { useCountryConfig } from '../../hooks/useCountryConfig';

const inputStyle = { backgroundColor: "var(--lm-s1)", border: "1px solid #23252a", color: "var(--lm-ink)", borderRadius: "8px", padding: "10px 12px", fontSize: "14px", width: "100%", outline: "none", transition: "border-color 0.15s" };
const inputErrorStyle = { ...inputStyle, borderColor: "#ef4444" };
const labelStyle = { display: "block", fontSize: "12px", fontWeight: "500", marginBottom: "6px", color: "var(--lm-ink-2)" };

const Step1CompanyInfo = ({ formData, loading, onNext, serverErrors, onClearServerError, countryCode }) => {
  const { t, i18n } = useTranslation();
  const { config } = useCountryConfig(countryCode || 'TN');
  const legalForms = config?.legal_forms || ['SARL', 'SA', 'SUARL', 'OTHER'];

  // Pick label in the current UI language, fall back to French then English
  const legalIdLabel = (() => {
    const lid = config?.legal_identifier;
    if (!lid) return 'Identifiant Fiscal';
    const lang = i18n.language?.split('-')[0] || 'fr';
    if (lang === 'ar' && lid.label_ar) return lid.label_ar;
    if (lang === 'en' && lid.label_en) return lid.label_en;
    return lid.label_fr || lid.label_en || 'Identifiant Fiscal';
  })();

  const legalIdPlaceholder = config?.legal_identifier?.placeholder || '';
  const legalIdRegex = config?.legal_identifier?.regex || null;

  const [data, setData] = useState({ company_name: formData.company_name || '', legal_form: formData.legal_form || legalForms[0] || 'SARL', tax_id: formData.tax_id || '', sector: formData.sector || '', company_size: formData.company_size || '' });
  const [errors, setErrors] = useState({});

  const sectorKeys = ['agriculture', 'banking', 'construction', 'commerce', 'education', 'energy', 'hospitality', 'manufacturing', 'it', 'healthcare', 'services', 'telecom', 'transport', 'tourism', 'other'];
  const companySizeKeys = ['1-10', '11-50', '51-200', '201-500', '500+'];

  const handleChange = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
    if (onClearServerError) onClearServerError(field);
  };

  // French SIRET numbers carry a Luhn checksum — "14 digits" alone lets
  // impossible numbers through (e.g. 12345678901234).
  const isLuhnValid = (digits) => {
    if (!/^\d+$/.test(digits)) return false;
    let total = 0;
    for (let i = 0; i < digits.length; i++) {
      let d = Number(digits[digits.length - 1 - i]);
      if (i % 2 === 1) { d *= 2; if (d > 9) d -= 9; }
      total += d;
    }
    return total % 10 === 0;
  };

  const validateTaxId = (value) => {
    const opts = { label: legalIdLabel };
    const compact = value.replace(/\s/g, '');
    if (!value.trim()) return t('registration.step1.validation.taxIdRequired', opts);
    if (legalIdRegex) { try { if (!new RegExp(legalIdRegex).test(compact)) return t('registration.step1.validation.taxIdInvalid', opts); } catch (_) {} }
    else if (value.trim().length < 3) return t('registration.step1.validation.taxIdInvalid', opts);
    if ((countryCode || 'TN') === 'FR' && !isLuhnValid(compact)) return t('registration.step1.validation.taxIdChecksum', opts);
    return null;
  };

  const validate = () => {
    const newErrors = {};
    if (!data.company_name.trim()) newErrors.company_name = t('registration.step1.validation.companyNameRequired');
    else if (data.company_name.length < 3) newErrors.company_name = t('registration.step1.validation.companyNameMinLength');
    if (!data.legal_form) newErrors.legal_form = t('registration.step1.validation.legalFormRequired');
    const taxIdError = validateTaxId(data.tax_id);
    if (taxIdError) newErrors.tax_id = taxIdError;
    if (!data.sector) newErrors.sector = t('registration.step1.validation.sectorRequired');
    if (!data.company_size) newErrors.company_size = t('registration.step1.validation.companySizeRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => { e.preventDefault(); if (validate()) onNext({ ...data, country_code: countryCode || 'TN' }); };

  const getLegalFormLabel = (form) => { const key = `legalForms.${form}`; const translated = t(key); return translated !== key ? translated : form; };

  const focusStyle = (e) => (e.target.style.borderColor = "#5e6ad2");
  const blurStyle = (e, hasError) => (e.target.style.borderColor = hasError ? "#ef4444" : "var(--lm-border)");

  return (
    <div className="p-8">
      <div className="mb-7">
        <h2 className="text-xl font-semibold mb-1" style={{ color: "var(--lm-ink)", letterSpacing: "-0.4px" }}>{t('registration.step1.title')}</h2>
        <p className="text-sm" style={{ color: "var(--lm-ink-2)" }}>{t('registration.step1.subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Company name */}
        <div>
          <label htmlFor="registration-company-name" style={labelStyle}>
            <Building2 className="w-3.5 h-3.5 inline ltr:mr-1.5 rtl:ml-1.5" style={{ color: "#5e6ad2" }} />
            {t('registration.step1.companyName')} *
          </label>
          <input id="registration-company-name" name="company_name" aria-invalid={!!errors.company_name} aria-describedby={errors.company_name ? "registration-company-name-error" : undefined}
            type="text" value={data.company_name} onChange={e => handleChange('company_name', e.target.value)}
            placeholder={t('registration.step1.companyNamePlaceholder')} dir="auto"
            style={errors.company_name ? inputErrorStyle : inputStyle}
            onFocus={focusStyle} onBlur={e => blurStyle(e, !!errors.company_name)} />
          {errors.company_name && <p id="registration-company-name-error" className="mt-1 text-xs" style={{ color: "#ef4444" }}>{errors.company_name}</p>}
        </div>

        {/* Legal form */}
        <div>
          <label htmlFor="registration-legal-form" style={labelStyle}>
            <FileText className="w-3.5 h-3.5 inline ltr:mr-1.5 rtl:ml-1.5" style={{ color: "#5e6ad2" }} />
            {t('registration.step1.legalForm')} *
          </label>
          <select id="registration-legal-form" name="legal_form" aria-invalid={!!errors.legal_form} aria-describedby={errors.legal_form ? "registration-legal-form-error" : undefined}
            value={data.legal_form} onChange={e => handleChange('legal_form', e.target.value)}
            style={errors.legal_form ? inputErrorStyle : inputStyle}
            onFocus={focusStyle} onBlur={e => blurStyle(e, !!errors.legal_form)}>
            {legalForms.map(form => <option key={form} value={form}>{getLegalFormLabel(form)}</option>)}
          </select>
          {errors.legal_form && <p id="registration-legal-form-error" className="mt-1 text-xs" style={{ color: "#ef4444" }}>{errors.legal_form}</p>}
        </div>

        {/* Tax ID */}
        <div>
          <label htmlFor="registration-tax-id" style={labelStyle}>
            <FileText className="w-3.5 h-3.5 inline ltr:mr-1.5 rtl:ml-1.5" style={{ color: "#5e6ad2" }} />
            {legalIdLabel} *
          </label>
          <input id="registration-tax-id" name="tax_id" aria-invalid={!!(errors.tax_id || serverErrors?.tax_id)}
            aria-describedby={(errors.tax_id || serverErrors?.tax_id) ? "registration-tax-id-error" : legalIdPlaceholder ? "registration-tax-id-help" : undefined}
            type="text" value={data.tax_id} onChange={e => handleChange('tax_id', e.target.value)}
            placeholder={legalIdPlaceholder || t('registration.step1.taxIdPlaceholder')} dir="ltr"
            style={(errors.tax_id || serverErrors?.tax_id) ? inputErrorStyle : inputStyle}
            onFocus={focusStyle} onBlur={e => blurStyle(e, !!(errors.tax_id || serverErrors?.tax_id))} />
          {(errors.tax_id || serverErrors?.tax_id) && <p id="registration-tax-id-error" className="mt-1 text-xs" style={{ color: "#ef4444" }}>{errors.tax_id || serverErrors?.tax_id}</p>}
          {legalIdPlaceholder && !errors.tax_id && !serverErrors?.tax_id && (
            <p id="registration-tax-id-help" className="mt-1 text-xs" style={{ color: "var(--lm-ink-3)" }}>{t('registration.step1.taxIdHelp')} — <span dir="ltr">{legalIdPlaceholder}</span></p>
          )}
        </div>

        {/* Sector */}
        <div>
          <label htmlFor="registration-sector" style={labelStyle}>
            <Briefcase className="w-3.5 h-3.5 inline ltr:mr-1.5 rtl:ml-1.5" style={{ color: "#5e6ad2" }} />
            {t('registration.step1.sector')} *
          </label>
          <select id="registration-sector" name="sector" aria-invalid={!!errors.sector} aria-describedby={errors.sector ? "registration-sector-error" : undefined}
            value={data.sector} onChange={e => handleChange('sector', e.target.value)}
            style={errors.sector ? inputErrorStyle : inputStyle}
            onFocus={focusStyle} onBlur={e => blurStyle(e, !!errors.sector)}>
            <option value="">{t('registration.step1.sectorPlaceholder')}</option>
            {sectorKeys.map(key => <option key={key} value={t(`sectors.${key}`)}>{t(`sectors.${key}`)}</option>)}
          </select>
          {errors.sector && <p id="registration-sector-error" className="mt-1 text-xs" style={{ color: "#ef4444" }}>{errors.sector}</p>}
        </div>

        {/* Company size */}
        <div>
          <label id="registration-company-size-label" style={labelStyle}>
            <Users className="w-3.5 h-3.5 inline ltr:mr-1.5 rtl:ml-1.5" style={{ color: "#5e6ad2" }} />
            {t('registration.step1.companySize')} *
          </label>
          <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-labelledby="registration-company-size-label" aria-describedby={errors.company_size ? "registration-company-size-error" : undefined}>
            {companySizeKeys.map(key => (
              <button key={key} type="button" onClick={() => handleChange('company_size', key)}
                role="radio" aria-checked={data.company_size === key}
                aria-label={t(`companySizes.${key}`)}
                className="px-3 py-2 rounded-lg text-xs font-medium transition-all"
                style={{
                  backgroundColor: data.company_size === key ? "rgba(94,106,210,0.15)" : "var(--lm-s2)",
                  border: `1px solid ${data.company_size === key ? "#5e6ad2" : "var(--lm-border)"}`,
                  color: data.company_size === key ? "#828fff" : "var(--lm-ink-2)",
                }}>
                {t(`companySizes.${key}`)}
              </button>
            ))}
          </div>
          {errors.company_size && <p id="registration-company-size-error" className="mt-1 text-xs" style={{ color: "#ef4444" }}>{errors.company_size}</p>}
        </div>

        {/* Submit */}
        <div className="flex justify-end pt-5" style={{ borderTop: "1px solid #23252a" }}>
          <button type="submit" disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: "#5e6ad2", color: "#fff", opacity: loading ? 0.5 : 1 }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.backgroundColor = "#828fff"; }}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = "#5e6ad2")}>
            {t('common.continue')}
            <ArrowRight className="w-4 h-4 rtl:rotate-180" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default Step1CompanyInfo;
